/*
 * Copyright (c) 2016 VMware, Inc. All Rights Reserved.
 * This software is released under MIT license.
 * The full license information can be found in LICENSE in the root directory of this project.
 */

import {Component, EventEmitter, forwardRef, Input, Output} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
/**
 * Private counter to generate unique IDs for the checkboxes, to bind the labels to them.
 */
let latestId = 0;
export class Checkbox implements ControlValueAccessor {
    // If our host has an ID attribute, we use this instead of our index.
    
    _id: string = (latestId++).toString();
/**
 * @return {?}
 */
public get id() {
        return `clr-checkbox-${this._id}`;
    }
public name: string = null;
public disabled: boolean = false;
public inline = false;
private _checked = false;
/**
 * @return {?}
 */
public get checked() {
        return this._checked;
    }
/**
 * @param {?} value
 * @return {?}
 */
public set checked(value: boolean) {
        if (value !== this._checked) {
            if (this._indeterminate) {
                this.setIndeterminate(false);
            }
            this.setChecked(value);
        }
    }
private _indeterminate: boolean = false;
/**
 * @return {?}
 */
public get indeterminate() {
        return this._indeterminate;
    }
/**
 * @param {?} value
 * @return {?}
 */
public set indeterminate(value: boolean) {
        if (this._indeterminate !== value) {
            if (this._checked) {
                this.setChecked(false);
            }
            this.setIndeterminate(value);
        }
    }
public indeterminateChange: EventEmitter<boolean> = new EventEmitter<boolean>(false);
/**
 * @param {?} value
 * @return {?}
 */
private setIndeterminate(value: boolean) {
        this._indeterminate = value;
        this.indeterminateChange.emit(this._indeterminate);
    }
/**
 * @param {?} value
 * @return {?}
 */
private setChecked(value: boolean) {
        this._checked = value;
        this.change.emit(this._checked);
    }
public change = new EventEmitter<boolean>(false);
/**
 * @return {?}
 */
public toggle() {
        this.checked = !this.checked;
        this.onChangeCallback(this.checked);
    }
/**
 * @param {?} value
 * @return {?}
 */
writeValue(value: any): void {
        if (value === null) {
            value = false;
        }
        if (value !== this.checked) {
            this.checked = value;
        }
    }
private onChangeCallback = (_: any) => {};
/**
 * @param {?} onChange
 * @return {?}
 */
registerOnChange(onChange: any): void {
        this.onChangeCallback = onChange;
    }
private onTouchedCallback = () => {};
/**
 * @param {?} onTouched
 * @return {?}
 */
registerOnTouched(onTouched: any): void {
        this.onTouchedCallback = onTouched;
    }
/**
 * @return {?}
 */
public touch() {
        this.onTouchedCallback();
    }
/**
 * @return {?}
 */
checkIndeterminateState(): void {
        if (!this.disabled) {
            this.toggle();
        }
    }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: "clr-checkbox",
    template: `
        <!--
            FIXME: We are not subscribed to the change event but the click event here.
            The reason for that is because checkboxes behave differently on IE & Edge.
            https://stackoverflow.com/a/19447939
            
            To fix that, we listen to every click event and then toggle the checkbox manually
            to make it behave the same way across the browsers we support.
            
            This works for cases when users toggle the checkbox using the keyboard too:
            https://stackoverflow.com/questions/27878940/spacebar-triggering-click-event-on-checkbox
        -->
        <input type="checkbox" [id]="id" [name]="name" [checked]="checked"
               [indeterminate]="indeterminate" [disabled]="disabled"
               (blur)="touch()" (click)="checkIndeterminateState()">
        <label [attr.for]="id">
            <ng-content></ng-content>
        </label>
    `,
    host: {"[class.checkbox]": "!inline", "[class.checkbox-inline]": "inline", "[class.disabled]": "disabled"},
    /*
     * This provider lets us declare our checkbox as a ControlValueAccessor,
     * which allows us to use [(ngModel)] directly on our component,
     * with all the automatic features wiring that come with it.
     */
    providers: [{provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => Checkbox), multi: true}]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'_id': [{ type: Input, args: ["id", ] },],
'name': [{ type: Input, args: ["name", ] },],
'disabled': [{ type: Input, args: ["clrDisabled", ] },],
'inline': [{ type: Input, args: ["clrInline", ] },],
'checked': [{ type: Input, args: ["clrChecked", ] },],
'indeterminate': [{ type: Input, args: ["clrIndeterminate", ] },],
'indeterminateChange': [{ type: Output, args: ["clrIndeterminateChange", ] },],
'change': [{ type: Output, args: ["clrCheckedChange", ] },],
};
}

function Checkbox_tsickle_Closure_declarations() {
/** @type {?} */
Checkbox.decorators;
/**
 * @nocollapse
 * @type {?}
 */
Checkbox.ctorParameters;
/** @type {?} */
Checkbox.propDecorators;
/** @type {?} */
Checkbox.prototype._id;
/** @type {?} */
Checkbox.prototype.name;
/** @type {?} */
Checkbox.prototype.disabled;
/** @type {?} */
Checkbox.prototype.inline;
/** @type {?} */
Checkbox.prototype._checked;
/** @type {?} */
Checkbox.prototype._indeterminate;
/** @type {?} */
Checkbox.prototype.indeterminateChange;
/** @type {?} */
Checkbox.prototype.change;
/** @type {?} */
Checkbox.prototype.onChangeCallback;
/** @type {?} */
Checkbox.prototype.onTouchedCallback;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
